import "./tree.css";

import React, { useMemo } from "react";

import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Container from "react-bootstrap/Container";
import Dropdown from "react-bootstrap/Dropdown";
import { Form } from "react-bootstrap";
import { fetchTreeLeaves } from "../../utils/phyloFetch.js";
import { buildConstraintSeqIds } from "../../utils/utils";
import { useApi } from "../../contexts/ApiProvider";
import { patientActions } from "../../actions/PatientActions";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "react-bootstrap/Spinner";


export default function TreeMenu({ seqIds, indexSeqId, noSeq, debug_mode }) {
  const api = useApi();

  const dispatch = useDispatch();
  var micro_id = useSelector((state) => state.patient.micro_id);
  const overlap_constraint = useSelector((state) => state.patient.tree.overlap_constraint);
  const snp_dist = useSelector((state) => state.patient.snp_distance);
  const showFutureSequences = useSelector(state => state.patient.tree.show_future_sequences);
  const overlap_level = useSelector((state) => state.patient.patient.overlap_level);
  const constraintLoading = useSelector((state) => state.patient.tree.constraint_loading);
  const normalizedSeqIds = Array.isArray(seqIds) ? seqIds : [];
  const seqIdsKey = normalizedSeqIds.join(",");
  const constraintSeqIds = useMemo(
    () => buildConstraintSeqIds(normalizedSeqIds, indexSeqId),
    [seqIdsKey, indexSeqId]
  );
  const canConstrain = constraintSeqIds.length > 0;

  const handleOverlapConstraintToggle = async (evt) => {
    if (!overlap_constraint) {
      if (!canConstrain) {
        return;
      }
      dispatch(patientActions.constraintLoadingStart());
      dispatch(patientActions.constrainByOverlap(true));
      dispatch(patientActions.constrainTree(constraintSeqIds));
      try {
        const constraints = await fetchTreeLeaves(api, micro_id, snp_dist);
        dispatch(patientActions.constrainOverlap(constraints));
      } catch (error) {
        console.error("Error constraining overlap:", error);
        dispatch(patientActions.constrainTree([]));
        dispatch(patientActions.constrainByOverlap(false));
        dispatch(patientActions.constrainOverlap([]));
      } finally {
        dispatch(patientActions.constraintLoadingEnd());
      }
    } else {
      dispatch(patientActions.constrainTree([]));
      dispatch(patientActions.constrainByOverlap(false));
      dispatch(patientActions.constrainOverlap([]));
    }
  };

  const handleShowFutureSequencesToggle = () => {
    dispatch(patientActions.showFutureSequences(!showFutureSequences));
  };

  const handleSnpDistanceSelect = async (evt) => {
    if (evt !== snp_dist) {
      if (overlap_constraint && canConstrain) {
        dispatch(patientActions.constraintLoadingStart());
        dispatch(patientActions.constrainTree(constraintSeqIds));
        try {
          const constraints = await fetchTreeLeaves(api, micro_id, evt);
          dispatch(patientActions.storeSnpDistance(evt));
          dispatch(patientActions.constrainOverlap(constraints));
        } catch (error) {
          console.error("Error updating overlap constraints:", error);
        } finally {
          dispatch(patientActions.constraintLoadingEnd());
        }
      } else {
        dispatch(patientActions.storeSnpDistance(evt));
      }
    }
  };

  // Filter out duplicates from the noSeq array
  const uniqueNoSeq = [...new Set(noSeq)];

  return (
    <>
      <Container
        fluid
        className="d-flex flex-row flex-wrap justify-content-start"
      >
        {" "}
        <div className="flex-grow-1 text-left">
          <Dropdown as={ButtonGroup} onSelect={handleSnpDistanceSelect}>
            <Dropdown.Toggle
                size="sm"
                className="blue-button"
                // disabled={adm}
                id="dropdown-custom-components"
              >
                <span style={{ fontWeight: 550 }}>SNP Distance</span> &nbsp;&nbsp;≤{snp_dist}
              </Dropdown.Toggle>
            <Dropdown.Menu
              id="tree-snp-distance-dropdown"
              title="SNP Distance"
              variant="light"
              size="sm"
            >
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map((distance) => (
                <Dropdown.Item
                  key={distance}
                  eventKey={distance.toString()}
                  active={snp_dist === distance.toString()}
                  className="custom-dropdown-item"
                >
                  {distance}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="flex-grow-1 text-left">
          <Form.Check
            type="switch"
            id="custom-switch"
            className="custom-switch"
            label={
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "purple",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                {`Show Case(s) with ${
                  overlap_level[0].toUpperCase() + overlap_level.slice(1)
                } Overlap Only`}
                {constraintLoading && (
                  <Spinner
                    as="span"
                    animation="border"
                    role="status"
                    size="sm"
                    style={{ width: "1rem", height: "1rem" }}
                  />
                )}
              </span>
            }
            checked={overlap_constraint}
            onChange={handleOverlapConstraintToggle}
            disabled={!canConstrain || constraintLoading}
            style={{
              paddingTop: "4.5px",
              marginLeft: "8px",
              marginRight: "15px",
            }}
          />
        </div>
        { debug_mode && (
          <div className="flex-grow-1 text-left">
            <Form.Check
              type="switch"
              id="future-sequence-switch"
              className="custom-switch"
              label="Show Future Sequences"
              checked={showFutureSequences}
              onChange={handleShowFutureSequencesToggle}
              style={{ paddingTop: "4.5px", marginLeft: "15px", marginRight: "15px"}}
            />
          </div>
        )}
      </Container>
    </>
  );
}
